import React, { useState, useEffect } from 'react';
import { Row, Col } from 'antd';
import intl from 'react-intl-universal';
import styles from './SubscriptionAddOnPricingTable.module.css';

const SubscriptionAddOnPricingTableHostForm = ({ productData, subscriptionAddOnPricingTable, pricingTableProducts, pricingModelCurrency }) => {
  const [row, setRow] = useState([]);
  const [column, setColumn] = useState([]);
  const [columnCount, setColumnCount] = useState(3);
  const cols = 24 / columnCount;

  const findProductName = productId => {
    const product = productData.find(p => p._id === productId);
    return product ? product.name : 'N/A';
  };

  useEffect(() => {
    if (subscriptionAddOnPricingTable && subscriptionAddOnPricingTable.length > 0) {
      // Set rows based on subscription add-on products
      setRow(subscriptionAddOnPricingTable.map((_, index) => index));

      // Set columns based on subscription products (packages) from the first add-on
      const firstAddOn = subscriptionAddOnPricingTable[0];
      if (firstAddOn && firstAddOn.subscriptionProducts) {
        const subscriptionProductsCount = firstAddOn.subscriptionProducts.length;
        setColumn(firstAddOn.subscriptionProducts.map((_, index) => index));
        setColumnCount(subscriptionProductsCount + 1 >= 3 ? subscriptionProductsCount + 1 : 3);
      }
    } else {
      setRow([]);
      setColumn([]);
      setColumnCount(3);
    }
  }, [subscriptionAddOnPricingTable]);

  // Get package name from pricingTableProducts or productData
  const getPackageName = subscriptionProductId => {
    // First try to find in pricingTableProducts
    if (pricingTableProducts && pricingTableProducts.length > 0) {
      const pricingProduct = pricingTableProducts.find(p => p.product === subscriptionProductId);
      if (pricingProduct) {
        return findProductName(pricingProduct.product);
      }
    }
    // Fallback to productData
    return findProductName(subscriptionProductId);
  };

  // Render pricing cell for a specific add-on product and package
  const pricing = (addOnIndex, packageIndex) => {
    const addOnProduct = subscriptionAddOnPricingTable[addOnIndex];
    if (!addOnProduct || !addOnProduct.subscriptionProducts) return null;

    const subscriptionProduct = addOnProduct.subscriptionProducts[packageIndex];
    if (!subscriptionProduct) return null;

    const listingPrice = subscriptionProduct.listingPrice || 0;

    return (
      <div style={{ paddingTop: '10px' }}>
        {pricingModelCurrency} {listingPrice.toFixed(2)} /listing
      </div>
    );
  };

  // Render a row for each subscription add-on product
  const renderProductRow = addOnIndex => {
    const addOnProduct = subscriptionAddOnPricingTable[addOnIndex];
    if (!addOnProduct) return null;

    const addOnProductName = findProductName(addOnProduct.subscriptionAddOnProduct);

    return (
      <Row gutter={16} key={addOnIndex} style={{ display: 'flex', justifyContent: 'center' }}>
        <Col span={cols} className={styles.pricingGridItem}>
          <div>{addOnProductName}</div>
        </Col>
        {column.map((_, packageIndex) => (
          <Col key={`pricing-${addOnIndex}-${packageIndex}`} span={cols} className={styles.pricingGridItem}>
            {pricing(addOnIndex, packageIndex)}
          </Col>
        ))}
      </Row>
    );
  };

  // Render package header
  const renderPackageHeader = packageIndex => {
    const firstAddOn = subscriptionAddOnPricingTable[0];
    if (!firstAddOn || !firstAddOn.subscriptionProducts || !firstAddOn.subscriptionProducts[packageIndex]) {
      return 'N/A';
    }

    const subscriptionProductId = firstAddOn.subscriptionProducts[packageIndex].subscriptionProduct;
    return getPackageName(subscriptionProductId);
  };

  // Check if we have data to display
  const hasData = subscriptionAddOnPricingTable && subscriptionAddOnPricingTable.length > 0;

  return (
    <>
      {hasData ? (
        <div className={styles.pricingGridContainer}>
          <div>
            <label>Subscription Add-On Pricing: </label>
          </div>
          <div className={styles.pricingGridWrapper}>
            <div className={styles.pricingGridInner}>
              {/* Header Row */}
              <Row gutter={16} className={styles.pricingGridRow}>
                <Col span={cols} className={styles.pricingGridHeader} style={{ padding: '0' }}>
                  <div className={styles.diagonalHeader}>
                    <span className={styles.productLabel}>Add-Ons</span>
                    <span className={styles.unitLabel}>Package</span>
                  </div>
                </Col>
                {column.map((_, packageIndex) => (
                  <Col key={`header-${packageIndex}`} span={cols} className={styles.pricingGridHeader}>
                    {renderPackageHeader(packageIndex)}
                  </Col>
                ))}
              </Row>
              {/* Product Rows */}
              {row.map(index => renderProductRow(index))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.label}>Subscription Add-On Pricing</div>
          <div className={styles.noPricingTable}>No Subscription Add-On Pricing Applied</div>
        </>
      )}
    </>
  );
};

export default SubscriptionAddOnPricingTableHostForm;
